/**
 * Lab bet-ticker flag column helpers (Company Master).
 * Live/Previous: simple flag on/off. Flag Final: category chips → finalFlagged.
 */

import type { FlagBetRequest } from '@/api/cmApi'
import type { FinalFlagCategory, TickerBet } from './domain'
import { FINAL_FLAG_CATEGORIES } from './domain'

export const FLAG_COMMENT_MAX = 2000

export function isFinalFlagCategory(raw: string): raw is FinalFlagCategory {
  return (FINAL_FLAG_CATEGORIES as readonly string[]).includes(raw)
}

/** Strip control chars (keep tab/newline) and cap length. */
export function normalizeFlagComment(raw: string): string {
  let out = ''
  for (const ch of raw) {
    const code = ch.charCodeAt(0)
    if (code < 32 && code !== 9 && code !== 10 && code !== 13) continue
    if (code === 127) continue
    out += ch
    if (out.length >= FLAG_COMMENT_MAX) break
  }
  return out
}

/** Save requires a non-empty comment after trim. */
export function isFlagCommentReady(raw: string): boolean {
  return normalizeFlagComment(raw).trim().length > 0
}

/** Payload to flip the Lab flag (clears final when unflagging). */
export function flagToggleRequest(bet: TickerBet): FlagBetRequest {
  if (bet.isFlagged) {
    return { _id: bet.id, flagged: false, finalFlagged: false }
  }
  return { _id: bet.id, flagged: true }
}

/** Lab Flag Final — set final + category (bet must already be flagged). */
export function flagFinalCategoryRequest(
  bet: TickerBet,
  category: FinalFlagCategory,
): FlagBetRequest {
  return {
    _id: bet.id,
    finalFlagged: true,
    finalFlagCategory: category,
  }
}

/** Lab Flag Final — clear final flag (keeps ordinary flagged). */
export function flagClearFinalRequest(bet: TickerBet): FlagBetRequest {
  return { _id: bet.id, finalFlagged: false, finalFlagCategory: '' }
}

export function flagCommentRequest(bet: TickerBet, comment: string): FlagBetRequest {
  return {
    _id: bet.id,
    flagged: bet.isFlagged,
    finalFlagged: bet.isFinalFlagged,
    comment: normalizeFlagComment(comment).trim(),
  }
}

/** Lab `flagBetClass` — off / flagged / final. */
export function flagState(
  bet: Pick<TickerBet, 'isFlagged' | 'isFinalFlagged'>,
): 'off' | 'flagged' | 'final' {
  if (bet.isFlagged && bet.isFinalFlagged) return 'final'
  if (bet.isFlagged) return 'flagged'
  return 'off'
}

export function flagTitle(bet: TickerBet): string {
  if (bet.isFinalFlagged) {
    return bet.isFinalFlaggedBy || bet.isFlaggedBy || 'Final flagged'
  }
  if (bet.isFlagged) return bet.isFlaggedBy || 'Flagged'
  return 'Flag bet'
}
